import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

import { create as createSchema } from "@blis/shared/schemas/test-result";
import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { mutationResponse } from "@/utils/trpc";
import type { TestResult } from "../../types";
import { AdminForm } from "../AdminForm";
import { TabLayout } from "../TabLayout";

type FormValues = z.infer<typeof createSchema>;

const colorOptions = [
  {
    label: "Red",
    value: "red",
    icon: <div className="size-4 rounded-full bg-red-600" data-slot="icon" />,
  },
  {
    label: "Green",
    value: "green",
    icon: <div className="size-4 rounded-full bg-green-600" data-slot="icon" />,
  },
  {
    label: "Blue",
    value: "blue",
    icon: <div className="size-4 rounded-full bg-blue-600" data-slot="icon" />,
  },
  {
    label: "Yellow",
    value: "yellow",
    icon: (
      <div className="size-4 rounded-full bg-yellow-600" data-slot="icon" />
    ),
  },
  {
    label: "Gray",
    value: "gray",
    icon: <div className="size-4 rounded-full bg-gray-600" data-slot="icon" />,
  },
];

export function ResultDetailForm({
  result,
  category = "TEXT",
}: {
  result?: TestResult;
  category?: string;
}) {
  const create = trpc.admin.testResult.create.useMutation();
  const update = trpc.admin.testResult.update.useMutation();

  const navigate = useNavigate();

  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      value: result?.value || "",
      description: result?.description || "",
      color: result?.color || null,
      category: result?.category || category,
    },
    resolver: zodResolver(createSchema),
  });

  const isOrganism = watch("category") === "ORGANISM";

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (result) {
      update.mutate(
        values,
        mutationResponse({
          errorTitle: "Error updating test result",
          successMessage: "Test result updated",
        }),
      );
    } else {
      create.mutate(
        values,
        mutationResponse({
          errorTitle: "Error adding test result",
          successMessage: "Test result added",
          onSuccess(data) {
            const encoded = encodeURIComponent(data?.value || "");
            navigate({
              to: "/admin/results/$id/edit",
              params: { id: encoded },
            });
          },
        }),
      );
    }
  };

  return (
    <TabLayout>
      <AdminForm onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            control={control}
            name="description"
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="result-description"
                {...field}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>{isOrganism ? "Name" : "Description"}</Label>
                <Input />
                <Description>
                  What is displayed in reports and in the UI
                </Description>
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="value"
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="result-code"
                {...field}
                value={field.value || ""}
                isInvalid={!!error}
                validationBehavior="aria"
                isDisabled={!!result}
              >
                <Label>Code</Label>
                <Input />
                <Description>
                  Must be unique, and cannot be changed after creation
                </Description>
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {!isOrganism && (
            <Controller
              control={control}
              name="color"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Select
                  {...field}
                  selectedKey={field.value}
                  onSelectionChange={field.onChange}
                  isInvalid={!!error}
                  validationBehavior="aria"
                  className="max-w-xs"
                >
                  <Label>Color</Label>
                  <Description>
                    Optional. Shown as colored badges in the UI and colored bold
                    text in reports
                  </Description>
                  <SelectTrigger isClearable />

                  <SelectList items={colorOptions}>
                    {(item) => (
                      <SelectItem
                        id={item.value ?? undefined}
                        textValue={item.label ?? ""}
                      >
                        <span className="inline-flex items-center gap-2">
                          {item.icon}
                          {item.label}
                        </span>
                      </SelectItem>
                    )}
                  </SelectList>
                  <FieldError>{error?.message}</FieldError>
                </Select>
              )}
            />
          )}
        </div>
        <Separator />
        <div className="flex justify-end gap-2">
          <Link
            to="/admin/results"
            search={isOrganism ? { t: "organisms" } : undefined}
            variant="outline"
            asButton
          >
            Cancel
          </Link>
          <Button
            type="submit"
            isPending={create.isPending || update.isPending}
          >
            Save
          </Button>
        </div>
      </AdminForm>
    </TabLayout>
  );
}
